/* Partner Lender Portal pages (Phase PP-2 · Feb 2026).
   5 page exports mounted under `/lender/*` shell. Theme: violet.
*/
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Landmark, Activity, AlertTriangle, RefreshCw, FileText, Banknote,
  Coins, Gavel, TrendingUp, ShieldCheck, ArrowUpRight, Pencil,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

function KpiTile({ label, value, sub, icon: Icon, tone = "slate", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const fmtMoney = n => n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtTime = s => { try { return new Date(s).toLocaleDateString(); } catch { return s || "—"; } };

const STATUS_TONE = {
  approved: "emerald", denied: "rose", pending: "amber",
  paid: "emerald", unpaid: "amber", overdue: "rose", void: "slate",
};

// ═══════════════════════════════════════════════════════════════════════
// OVERVIEW
// ═══════════════════════════════════════════════════════════════════════
export function LenderOverview() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/lender/overview", { params: { since_days: days } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="lender-overview" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <Landmark className="w-6 h-6 text-violet-500" /> Lender Overview
          </h1>
          <p className="text-sm text-slate-600 mt-1">Active loan book + recent decisions + delinquency snapshot.</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="lender-overview-window"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="lender-overview-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/> Refresh
          </Button>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="lender-overview-kpis">
            <KpiTile testid="lender-kpi-grants" label="Active grants" value={data.loanbook.active_grants} icon={ShieldCheck} tone="violet" />
            <KpiTile testid="lender-kpi-limit" label="Credit extended" value={fmtMoney(data.loanbook.limit_sum_usd)} icon={Banknote} tone="emerald" />
            <KpiTile testid="lender-kpi-outstanding" label="Outstanding" value={fmtMoney(data.disbursements_outstanding.amount_usd)} sub={`${data.disbursements_outstanding.count} invoices`} icon={Coins} tone="sky" />
            <KpiTile testid="lender-kpi-approval" label="Approval rate" value={`${data.decisions_window.approval_pct}%`} sub={`${data.decisions_window.approved} of ${data.decisions_window.applications}`} icon={TrendingUp} tone="emerald" />
            <KpiTile testid="lender-kpi-past-due" label="Past due" value={data.delinquencies.past_due_invoices} icon={AlertTriangle} tone={data.delinquencies.past_due_invoices ? "rose" : "slate"} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border bg-white p-4">
              <div className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Activity className="w-4 h-4"/> Decisions (last {days}d)</div>
              <div className="grid grid-cols-3 gap-3">
                <KpiTile testid="lender-decisions-apps" label="Applications" value={data.decisions_window.applications} tone="slate" />
                <KpiTile testid="lender-decisions-approved" label="Approved" value={data.decisions_window.approved} tone="emerald" />
                <KpiTile testid="lender-decisions-denied" label="Denied" value={data.decisions_window.denied} tone="rose" />
              </div>
            </div>
            <div className="rounded-2xl border bg-white p-4">
              <div className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Gavel className="w-4 h-4"/> Compliance</div>
              <KpiTile testid="lender-compliance-adverse" label="Adverse notices issued" value={data.compliance.adverse_notices_window} sub="ECOA / FCRA · auto-issued by AI underwriter" tone="amber" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LOAN BOOK
// ═══════════════════════════════════════════════════════════════════════
export function LenderLoanbook() {
  const [rows, setRows] = useState([]);
  const [byStatus, setByStatus] = useState({});
  const [status, setStatus] = useState("all");
  const [terms, setTerms] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (terms !== "all") params.terms = terms;
    const { data } = await api.get("/partner/lender/loanbook", { params });
    setRows(data.rows);
    setByStatus(data.by_status || {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, terms]);

  return (
    <div data-testid="lender-loanbook" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><FileText className="w-5 h-5"/> Loan book</h1>
      <div className="grid grid-cols-4 gap-3" data-testid="loanbook-strip">
        <KpiTile testid="loanbook-approved"  label="Approved" value={byStatus.approved || 0} tone="emerald" />
        <KpiTile testid="loanbook-pending"   label="Pending"  value={byStatus.pending || 0} tone="amber" />
        <KpiTile testid="loanbook-denied"    label="Denied"   value={byStatus.denied || 0} tone="rose" />
        <KpiTile testid="loanbook-total"     label="Total"    value={rows.length} tone="slate" />
      </div>

      <div className="flex gap-2 items-center rounded-2xl border bg-white px-4 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="loanbook-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["approved", "pending", "denied"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={terms} onValueChange={setTerms}>
          <SelectTrigger className="w-36 h-9" data-testid="loanbook-filter-terms"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All terms</SelectItem>
            {["net15", "net30", "net60", "net90"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Terms</div>
          <div className="col-span-2">Rate</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Created</div>
        </div>
        <div data-testid="loanbook-table">
          {rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="loanbook-empty">No credit applications.</div>}
          {rows.map(r => {
            const tone = STATUS_TONE[r.status] || "slate";
            return (
              <button
                key={r.id}
                data-testid={`loanbook-row-${r.id}`}
                onClick={() => setOpen(r)}
                className="w-full text-left grid grid-cols-12 px-4 py-2 border-b text-sm hover:bg-slate-50/70"
              >
                <div className="col-span-3 truncate">{r.buyer_name || r.buyer_id?.slice(0,8) + "…"}</div>
                <div className="col-span-2 tabular-nums">{fmtMoney(r.amount)}</div>
                <div className="col-span-2 text-xs"><Badge variant="outline">{r.terms || "—"}</Badge></div>
                <div className="col-span-2 text-xs text-slate-600">{r.rate ? `${(r.rate*100).toFixed(2)}%` : "—"}</div>
                <div className="col-span-2 text-xs"><span className={`px-2 py-0.5 rounded-full bg-${tone}-50 text-${tone}-700 border border-${tone}-200 text-[10px]`}>{r.status}</span></div>
                <div className="col-span-1 text-right text-xs text-slate-500">{fmtTime(r.created_at)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <BuyerDetailDialog buyerId={open?.buyer_id} onClose={() => setOpen(null)} />
    </div>
  );
}

function BuyerDetailDialog({ buyerId, onClose }) {
  const [data, setData] = useState(null);
  const [showLimit, setShowLimit] = useState(false);
  useEffect(() => {
    if (!buyerId) { setData(null); return; }
    api.get(`/partner/lender/buyers/${buyerId}`).then(r => setData(r.data));
  }, [buyerId]);
  if (!buyerId) return null;
  return (
    <Dialog open={!!buyerId} onOpenChange={onClose}>
      <DialogContent data-testid="buyer-detail-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Buyer · {data?.buyer?.company_name || data?.buyer?.email || buyerId.slice(0,8) + "…"}</DialogTitle>
          <DialogDescription>Credit history + financed invoices + adverse notices</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Email</div>{data.buyer.email}</div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Country</div>{data.buyer.country || "—"}</div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Active grant</div>{data.buyer.ai_credit_grant?.active ? "Yes" : "No"}</div>
              <div className="rounded-lg border bg-slate-50 px-3 py-2"><div className="text-[10px] uppercase text-slate-500">Current limit</div>{fmtMoney(data.buyer.ai_credit_grant?.limit_usd || 0)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-700 mb-1.5">Credit applications ({data.credit_apps.length})</div>
              <div className="rounded-lg border divide-y max-h-44 overflow-auto" data-testid="buyer-detail-apps">
                {data.credit_apps.length === 0 && <div className="text-sm text-slate-500 px-3 py-3">None</div>}
                {data.credit_apps.map(a => (
                  <div key={a.id} className="px-3 py-2 text-sm grid grid-cols-12 gap-2">
                    <div className="col-span-4 text-xs text-slate-500">{fmtTime(a.created_at)}</div>
                    <div className="col-span-3 tabular-nums">{fmtMoney(a.amount)}</div>
                    <div className="col-span-3 text-xs">{a.terms}</div>
                    <div className="col-span-2 text-right text-xs">{a.status}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-700 mb-1.5">Financed invoices ({data.financed_invoices.length})</div>
              <div className="rounded-lg border divide-y max-h-32 overflow-auto" data-testid="buyer-detail-financed">
                {data.financed_invoices.length === 0 && <div className="text-sm text-slate-500 px-3 py-3">None</div>}
                {data.financed_invoices.map(i => (
                  <div key={i.id} className="px-3 py-2 text-sm grid grid-cols-12">
                    <div className="col-span-3 font-mono text-xs">{i.code}</div>
                    <div className="col-span-3 tabular-nums">{fmtMoney(i.amount)}</div>
                    <div className="col-span-3 text-xs">{i.terms}</div>
                    <div className="col-span-3 text-right text-xs">{i.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowLimit(true)} data-testid="buyer-detail-limit-btn">
            <Pencil className="w-3.5 h-3.5 mr-1.5"/> Adjust limit
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
        <LimitDialog buyer={data?.buyer} open={showLimit} onClose={() => setShowLimit(false)} onSaved={() => setShowLimit(false)} />
      </DialogContent>
    </Dialog>
  );
}

function LimitDialog({ buyer, open, onClose, onSaved }) {
  const [limit, setLimit] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && buyer) {
      setLimit(String(buyer.ai_credit_grant?.limit_usd || 0));
      setReason("");
    }
  }, [open, buyer?.id]);
  if (!buyer) return null;
  const submit = async () => {
    const n = Number(limit);
    if (!Number.isFinite(n) || n < 0 || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.patch(`/partner/lender/buyers/${buyer.id}/limit`, { new_limit_usd: n, reason });
      toast.success("Limit updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Update failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="limit-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust credit limit</DialogTitle>
          <DialogDescription>{buyer.company_name || buyer.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>New limit (USD)</Label><Input type="number" value={limit} onChange={e => setLimit(e.target.value)} data-testid="limit-input" min={0} /></div>
          <div><Label>Reason</Label><Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="limit-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="limit-submit" onClick={submit} disabled={busy || reason.trim().length < 3}>{busy ? "Saving…" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DISBURSEMENTS
// ═══════════════════════════════════════════════════════════════════════
export function LenderDisbursements() {
  const [data, setData] = useState({ rows: [], total_amount_usd: 0 });
  useEffect(() => { api.get("/partner/lender/disbursements").then(r => setData(r.data)); }, []);
  return (
    <div data-testid="lender-disbursements" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><ArrowUpRight className="w-5 h-5"/> Disbursements</h1>
      <div className="grid grid-cols-3 gap-3">
        <KpiTile testid="disb-count" label="Invoices" value={data.rows.length} tone="slate" />
        <KpiTile testid="disb-total" label="Total disbursed" value={fmtMoney(data.total_amount_usd)} tone="violet" />
        <KpiTile testid="disb-avg" label="Avg invoice" value={fmtMoney(data.rows.length ? data.total_amount_usd / data.rows.length : 0)} tone="sky" />
      </div>
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">Code</div>
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Terms</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Due</div>
        </div>
        <div data-testid="disb-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="disb-empty">No financed invoices.</div>}
          {data.rows.map(r => {
            const tone = STATUS_TONE[r.status] || "slate";
            return (
              <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
                <div className="col-span-2 font-mono text-xs">{r.code}</div>
                <div className="col-span-3 truncate">{r.buyer_name}</div>
                <div className="col-span-2 tabular-nums">{fmtMoney(r.amount)}</div>
                <div className="col-span-2 text-xs"><Badge variant="outline">{r.terms}</Badge></div>
                <div className="col-span-2 text-xs"><span className={`px-2 py-0.5 rounded-full bg-${tone}-50 text-${tone}-700 border border-${tone}-200 text-[10px]`}>{r.status}</span></div>
                <div className="col-span-1 text-right text-xs text-slate-500">{fmtTime(r.due_date)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// REPAYMENTS
// ═══════════════════════════════════════════════════════════════════════
export function LenderRepayments() {
  const [data, setData] = useState({ rows: [], total_amount_usd: 0 });
  useEffect(() => { api.get("/partner/lender/repayments").then(r => setData(r.data)); }, []);
  return (
    <div data-testid="lender-repayments" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><Banknote className="w-5 h-5"/> Repayments</h1>
      <div className="grid grid-cols-2 gap-3">
        <KpiTile testid="repay-count" label="Repayment txns" value={data.rows.length} tone="slate" />
        <KpiTile testid="repay-total" label="Total repaid" value={fmtMoney(data.total_amount_usd)} tone="emerald" />
      </div>
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">When</div>
          <div className="col-span-2">User ref</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Method</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        <div data-testid="repay-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="repay-empty">No repayments yet.</div>}
          {data.rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
              <div className="col-span-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</div>
              <div className="col-span-2 font-mono text-xs">{r.user_ref}</div>
              <div className="col-span-2 text-xs">{r.type}</div>
              <div className="col-span-2 text-xs">{r.method || "—"}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.amount)}</div>
              <div className="col-span-1 text-right text-xs">{r.status}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DELINQUENCIES
// ═══════════════════════════════════════════════════════════════════════
export function LenderDelinquencies() {
  const [data, setData] = useState({ rows: [], buckets: {} });
  useEffect(() => { api.get("/partner/lender/delinquencies").then(r => setData(r.data)); }, []);
  return (
    <div data-testid="lender-delinquencies" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><AlertTriangle className="w-5 h-5"/> Delinquencies</h1>
      <div className="grid grid-cols-4 gap-3" data-testid="delq-buckets">
        <KpiTile testid="delq-1-15"   label="1–15 days"   value={data.buckets["1_15"] || 0} tone="amber" />
        <KpiTile testid="delq-16-30"  label="16–30 days"  value={data.buckets["16_30"] || 0} tone="amber" />
        <KpiTile testid="delq-31-60"  label="31–60 days"  value={data.buckets["31_60"] || 0} tone="rose" />
        <KpiTile testid="delq-61"     label="61+ days"    value={data.buckets["61_plus"] || 0} tone="rose" />
      </div>
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-2">Invoice</div>
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Terms</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1 text-right">Days</div>
        </div>
        <div data-testid="delq-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="delq-empty">All current — no past-due invoices.</div>}
          {data.rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
              <div className="col-span-2 font-mono text-xs">{r.code}</div>
              <div className="col-span-3 truncate">{r.buyer_name}</div>
              <div className="col-span-2 tabular-nums">{fmtMoney(r.amount)}</div>
              <div className="col-span-2 text-xs"><Badge variant="outline">{r.terms}</Badge></div>
              <div className="col-span-2 text-xs text-slate-500">{fmtTime(r.due_date)}</div>
              <div className="col-span-1 text-right text-xs"><span className={`px-2 py-0.5 rounded-full text-[10px] ${r.days_past_due > 60 ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{r.days_past_due}d</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ADVERSE NOTICES
// ═══════════════════════════════════════════════════════════════════════
export function LenderAdverseNotices() {
  const [data, setData] = useState({ rows: [] });
  useEffect(() => { api.get("/partner/lender/adverse-notices").then(r => setData(r.data)); }, []);
  return (
    <div data-testid="lender-adverse" className="space-y-4">
      <h1 className="display text-2xl tracking-tight flex items-center gap-2"><Gavel className="w-5 h-5"/> Adverse notices</h1>
      <KpiTile testid="adverse-total" label="Total notices" value={data.rows.length} tone="amber" />
      <div className="rounded-2xl border bg-white">
        <div className="grid grid-cols-12 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <div className="col-span-3">When</div>
          <div className="col-span-3">Buyer</div>
          <div className="col-span-2">Kind</div>
          <div className="col-span-2">Decision</div>
          <div className="col-span-1">Prev → New</div>
          <div className="col-span-1 text-right">Email</div>
        </div>
        <div data-testid="adverse-table">
          {data.rows.length === 0 && <div className="text-center text-sm text-slate-500 py-10" data-testid="adverse-empty">No adverse notices.</div>}
          {data.rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b text-sm">
              <div className="col-span-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</div>
              <div className="col-span-3 truncate">{r.buyer_company}</div>
              <div className="col-span-2 text-xs">{r.kind}</div>
              <div className="col-span-2 text-xs"><Badge variant="outline">{r.decision}</Badge></div>
              <div className="col-span-1 text-xs tabular-nums">{fmtMoney(r.previous_limit_usd)} → {fmtMoney(r.new_limit_usd)}</div>
              <div className="col-span-1 text-right">{r.email_sent ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 inline"/> : <span className="text-rose-500 text-xs">✗</span>}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
